import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	distinctUntilChanged,
	filter,
	map,
	merge,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';
import { UniversityReviewsApiService } from '../services/university-reviews-api.service';
import { UniversityApiService } from '../services/university-api.service';
import {
	fetchUniversity,
	updateUniversity,
	loadStateUniversityFailure,
	updateUniversityUrl,
	updateUniversityRatingStats,
	fetchUniversityRatingStats,
	fetchClearingUniversity,
	updateClearingUniversity,
	fetchUniByProduct,
	updateUniByProduct,
} from './university.actions';
import {
	getStateClearingUniversity,
	getStateUniversity,
	getStateUniversityUrl,
} from './university.selectors';

@Injectable()
export class StateUniversityEffects {
	constructor(
		private readonly _actions$: Actions,
		private _store: Store,
		private _universityApiSrv: UniversityApiService,
		private _reviewsSrv: UniversityReviewsApiService,
	) {}

	fetchUniversity = createEffect(() =>
		this._actions$.pipe(
			ofType(fetchUniversity),
			distinctUntilChanged(),
			withLatestFrom(
				this._store.select(getStateUniversity),
				this._store.select(getStateUniversityUrl), // university url from store
			),
			filter(([payload, universityData, universityUrl]) => {
				// continue the stream if no data
				// or if the current universityId !== universityId from store
				if (!universityData || payload.universityUrl !== universityUrl) {
					return true;
				}
				// cancel the stream
				return false;
			}),
			tap(([payload]) => {
				this._store.dispatch(updateUniversityUrl(payload));
			}),
			switchMap(([payload]) =>
				// the of operator is used for resetting the data in store
				// so the UI is updated and avoid printing the current data from store
				merge(of(null), this._universityApiSrv.getUniversity(payload)),
			),
			map((university) => updateUniversity(university)),
			catchError((error) => {
				return loadStateUniversityFailure(error);
			}),
		),
	);

	fetchClearingUniversity = createEffect(() =>
		this._actions$.pipe(
			ofType(fetchClearingUniversity),
			distinctUntilChanged(),
			withLatestFrom(
				this._store.select(getStateClearingUniversity),
				this._store.select(getStateUniversityUrl), // university url from store
			),
			switchMap(([payload]) =>
				// the of operator is used for resetting the data in store
				// so the UI is updated and avoid printing the current data from store
				merge(
					of(null),
					this._universityApiSrv.getClearingUniversity(payload.uniSlug),
				),
			),
			map((university) => updateClearingUniversity(university)),
			catchError((error) => {
				return loadStateUniversityFailure(error);
			}),
		),
	);

	// when switching tabs between UG and PG update the relevant data for the current profile
	fetchProductBasedUniversity = createEffect(() =>
		this._actions$.pipe(
			ofType(fetchUniByProduct),
			distinctUntilChanged(),
			withLatestFrom(this._store.select(getStateUniversity)),
			filter(([, universityData]) => {
				// continue the stream if data
				if (universityData && universityData['universityGallery']) {
					return true;
				}
				// cancel the stream
				return false;
			}),
			switchMap(([payload]) =>
				combineLatest([
					this._universityApiSrv.getUniversityOverview(payload),
					this._universityApiSrv.getUniversityGallery(payload),
				]),
			),
			map(([overview, universityGallery]) => {
				return updateUniByProduct({
					overview,
					universityGallery,
				});
			}),
			catchError((error) => of(loadStateUniversityFailure(error))),
		),
	);

	fetchUniversityRatingStats = createEffect(() =>
		this._actions$.pipe(
			ofType(fetchUniversityRatingStats),
			distinctUntilChanged(),
			switchMap((payload) => this._reviewsSrv.getUniversityRatingStats(payload)),
			map((universityRatings) => updateUniversityRatingStats(universityRatings)),
			catchError((error) => of(loadStateUniversityFailure(error))),
		),
	);
}
